import { useQuery } from '@tanstack/react-query';
import { Button, Card, Col, Row, Skeleton, Statistic, Typography } from 'antd';
import { FaBox, FaCheck, FaClock, FaMoneyBillWave, FaWallet, FaExchangeAlt } from 'react-icons/fa';
import { useState } from 'react';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { RetailerSummaryReport } from '../../types/reports.types';
import RetailerWithdrawModal from '../../components/RetailerWithdrawModal';
import { ReactNode } from 'react';

const { Title } = Typography;

interface DashboardCard {
	title: string;
	value: number;
	icon: ReactNode;
	color: string;
	loading: boolean;
	formatter: (value: number | string) => string | number;
	suffix?: string;
	action?: ReactNode;
}

const RetailerDashboard = () => {
	const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

	// Fetch retailer summary data
	const { data, isLoading, error, refetch } = useQuery<{ results: RetailerSummaryReport }>({
		queryKey: ['retailerSummary'],
		queryFn: async () => {
			const { data } = await axios.get('/reports/retailers/summary', { withCredentials: true });
			return data;
		},
	});

	const summaryData = data?.results;

	// Format currency for display
	const formatCurrency = (value: number | string) => {
		const numValue = typeof value === 'string' ? parseFloat(value) : value;
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'UGX',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(numValue);
	};

	// Format number with thousand separators
	const formatNumber = (value: number | string) => {
		const numValue = typeof value === 'string' ? parseFloat(value) : value;
		return numValue.toLocaleString();
	};

	// Card configuration for easy customization
	const salesCards: DashboardCard[] = [
		{
			title: 'Devices Sold',
			value: summaryData?.devices_sold || 0,
			icon: <FaBox size={24} />,
			color: '#1890ff',
			loading: isLoading,
			suffix: 'units',
			formatter: formatNumber,
		},
		{
			title: 'Total Amount',
			value: summaryData?.total_amount || 0,
			icon: <FaMoneyBillWave size={24} />,
			color: '#52c41a',
			loading: isLoading,
			formatter: formatCurrency,
		},
		{
			title: 'Paid Amount',
			value: summaryData?.paid_amount || 0,
			icon: <FaCheck size={24} />,
			color: '#13c2c2',
			loading: isLoading,
			formatter: formatCurrency,
		},
		{
			title: 'Pending Amount',
			value: summaryData?.pending_amount || 0,
			icon: <FaClock size={24} />,
			color: '#fa8c16',
			loading: isLoading,
			formatter: formatCurrency,
		},
	];

	// Balance cards configuration
	const balanceCards: DashboardCard[] = [
		{
			title: 'Amount Withdrawn',
			value: summaryData?.amount_withdrawn || 0,
			icon: <FaExchangeAlt size={24} />,
			color: '#722ed1',
			loading: isLoading,
			formatter: formatCurrency,
		},
		{
			title: 'Available for Withdrawal',
			value: summaryData?.balance_available_for_withdrawal || 0,
			icon: <FaWallet size={24} />,
			color: '#eb2f96',
			loading: isLoading,
			formatter: formatCurrency,
			action:
				(summaryData?.balance_available_for_withdrawal || 0) > 0 ? (
					<Button
						type="primary"
						size="small"
						onClick={() => setIsWithdrawModalOpen(true)}
						className="mt-2"
					>
						Withdraw
					</Button>
				) : null,
		},
	];

	const handleWithdrawSuccess = () => {
		refetch(); // Refresh summary data after successful withdrawal
	};

	// Render card component
	const renderCard = (card: DashboardCard, index: number) => (
		<Col
			xs={24}
			sm={12}
			lg={card.action ? 12 : 6}
			key={index}
		>
			<Card
				className="h-full overflow-hidden transition-all duration-300 hover:shadow-md"
				style={{ borderTop: `2px solid ${card.color}` }}
				styles={{
					body: {
						padding: '20px',
					},
				}}
			>
				{card.loading ? (
					<Skeleton
						active
						paragraph={{ rows: 1 }}
					/>
				) : (
					<>
						<div className="flex items-center justify-between">
							<div className="text-lg font-medium text-gray-500">{card.title}</div>
							<div
								className="flex h-10 w-10 items-center justify-center rounded-full"
								style={{ backgroundColor: `${card.color}15` }}
							>
								<span style={{ color: card.color }}>{card.icon}</span>
							</div>
						</div>
						<Statistic
							value={card.value}
							valueStyle={{ fontSize: '28px', fontWeight: 'bold', color: '#111827' }}
							formatter={card.formatter}
							suffix={card.suffix}
						/>
						{card.action}
					</>
				)}
			</Card>
		</Col>
	);

	return (
		<AppLayout
			title="Dashboard"
			subtitle="Manage your activities and view statistics here."
		>
			{error ? (
				<div className="rounded-md bg-red-50 p-4 text-red-500">Unable to load dashboard data. Please try again later.</div>
			) : (
				<>
					<div className="mb-8">
						<Title level={4}>Sales Overview</Title>
						<p className="text-gray-500">Your sales performance at a glance</p>
					</div>

					<Row gutter={[16, 16]}>{salesCards.map((card, index) => renderCard(card, index))}</Row>

					<div className="mb-8 mt-12">
						<Title level={4}>Balance Information</Title>
						<p className="text-gray-500">Your current balance and withdrawal status</p>
					</div>

					<Row gutter={[16, 16]}>{balanceCards.map((card, index) => renderCard(card, index + salesCards.length))}</Row>

					{/* Withdrawal Modal */}
					<RetailerWithdrawModal
						open={isWithdrawModalOpen}
						onClose={() => setIsWithdrawModalOpen(false)}
						availableBalance={summaryData?.balance_available_for_withdrawal || 0}
						onSuccess={handleWithdrawSuccess}
					/>
				</>
			)}
		</AppLayout>
	);
};

export default RetailerDashboard;
